import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, MessageCircle, MapPinned, Bot, RefreshCcw, Globe,
  ChevronRight, AlertCircle, Sparkles, BookOpen, ArrowRight,
  CheckCircle, Clock,
} from 'lucide-react';
import { getDashboardExperiences, getInteractionsByExperience } from '../services/legacyApiService';
import { LegacyExperience, LegacyInteraction } from '../types/legacy';
import {
  applyExperienceCoverOverrides,
  resolveExperienceCoverCandidates,
  resolveAvatarUrl,
} from '../utils/imageUtils';

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [interactionsLoading, setInteractionsLoading] = useState(false);
  const [experiences, setExperiences] = useState<LegacyExperience[]>([]);
  const [interactions, setInteractions] = useState<LegacyInteraction[]>([]);
  const [selectedExperienceId, setSelectedExperienceId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [brokenMediaUrls, setBrokenMediaUrls] = useState<Record<string, boolean>>({});

  const load = async (focusExperienceId?: number | null) => {
    try {
      setLoading(true);
      setError(null);
      const exp = applyExperienceCoverOverrides(await getDashboardExperiences());
      setExperiences(exp);

      const targetId = focusExperienceId ?? null;
      setSelectedExperienceId(targetId);

      if (targetId) {
        setInteractionsLoading(true);
        try {
          const selectedInteractions = await getInteractionsByExperience({ idExperience: targetId });
          setInteractions(selectedInteractions);
        } finally {
          setInteractionsLoading(false);
        }
      } else {
        setInteractions([]);
      }
    } catch (err) {
      console.error(err);
      setError('Impossibile caricare i dati. Verifica la connessione API della piattaforma.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const selectedExp = experiences.find((e) => e.id === selectedExperienceId);

  const quickActions = [
    { label: 'Nuova esperienza', desc: 'Crea un percorso culturale', href: '/esperienze', icon: <Globe size={20} />, color: 'from-blue-500 to-cyan-500' },
    { label: 'Configura Genius Loci', desc: 'Costruisci la guida IA', href: '/genius-loci', icon: <Bot size={20} />, color: 'from-amber-500 to-orange-500' },
    { label: 'Gestisci Intent', desc: 'Aggiungi risposte e POI', href: '/interazioni', icon: <MessageCircle size={20} />, color: 'from-green-500 to-emerald-500' },
    { label: 'Analytics', desc: 'Monitora interazioni e utilizzo', href: '/analytics', icon: <Activity size={20} />, color: 'from-fuchsia-500 to-indigo-500' },
    { label: 'Guida alla piattaforma', desc: 'Come fare ogni cosa', href: '/documentazione', icon: <BookOpen size={20} />, color: 'from-violet-500 to-purple-500' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Dashboard</h1>
          <p className="text-text-secondary mt-1">Benvenuto in Glooci Platform 3.0 — CMS esperienze culturali IA</p>
        </div>
        <button
          type="button"
          className="btn-outline inline-flex items-center gap-2 self-start"
          onClick={() => void load(selectedExperienceId)}
          disabled={loading}
        >
          <RefreshCcw size={16} className={loading ? 'animate-spin' : ''} />
          Aggiorna
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-error/30 bg-error/10 p-4 text-error flex items-start gap-3">
          <AlertCircle size={18} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-sm">Errore di connessione</p>
            <p className="text-xs mt-0.5 opacity-80">{error}</p>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div>
        <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">Azioni rapide</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          {quickActions.map((action) => (
            <Link
              key={action.href}
              to={action.href}
              className="card p-5 hover:shadow-lg transition-all duration-200 group flex items-center gap-4"
            >
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center text-white shrink-0 group-hover:scale-110 transition-transform`}>
                {action.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-text-primary text-sm">{action.label}</p>
                <p className="text-xs text-text-secondary truncate">{action.desc}</p>
              </div>
              <ArrowRight size={16} className="text-text-secondary/40 group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Ultime esperienze */}
        <div className="card overflow-hidden">
          <div className="p-5 border-b border-border flex items-center justify-between">
            <h2 className="font-semibold text-text-primary">Ultime esperienze</h2>
            <Link to="/esperienze" className="text-xs text-primary hover:underline flex items-center gap-1">
              Vedi tutte <ChevronRight size={14} />
            </Link>
          </div>
          <div className="divide-y divide-border">
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="p-4 flex items-center gap-3 animate-pulse">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-2/3" />
                      <div className="h-3 bg-gray-200 rounded w-1/3" />
                    </div>
                  </div>
                ))
              : experiences.slice(0, 5).map((exp) => {
                  const coverCandidates = resolveExperienceCoverCandidates(exp);
                  const avatarUrl = resolveAvatarUrl(exp.avatar);
                  const safeAvatar = avatarUrl && !brokenMediaUrls[avatarUrl] ? avatarUrl : null;
                  const cover = coverCandidates.find((url) => !brokenMediaUrls[url]) || safeAvatar || null;
                  return (
                    <div key={exp.id} className="p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gradient-to-br from-primary/10 to-accent/10 shrink-0">
                        {cover ? (
                          <img
                            key={cover}
                            src={cover}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const img = e.currentTarget as HTMLImageElement;
                              img.style.display = 'none';
                              const failedUrl = img.currentSrc || img.src;
                              if (failedUrl) {
                                setBrokenMediaUrls((prev) => (prev[failedUrl] ? prev : { ...prev, [failedUrl]: true }));
                              }
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Globe size={20} className="text-primary/30" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-text-primary text-sm truncate">{exp.name}</p>
                        <p className="text-xs text-text-secondary truncate">{exp.city || 'Posizione non specificata'}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${exp.isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {exp.isPublished ? 'Pubblica' : 'Bozza'}
                      </span>
                    </div>
                  );
                })}
          </div>
        </div>

        {/* Intent dell'esperienza selezionata */}
        <div className="card overflow-hidden">
          <div className="p-5 border-b border-border">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-text-primary">Intent per esperienza</h2>
              <Link to="/interazioni" className="text-xs text-primary hover:underline flex items-center gap-1">
                Gestisci <ChevronRight size={14} />
              </Link>
            </div>
            <select
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={selectedExperienceId ?? ''}
              onChange={async (e) => {
                const value = e.target.value ? Number(e.target.value) : null;
                setSelectedExperienceId(value);
                if (!value) { setInteractions([]); return; }
                try {
                  setInteractionsLoading(true);
                  const ints = await getInteractionsByExperience({ idExperience: value });
                  setInteractions(ints);
                } catch (err) {
                  console.error(err);
                } finally {
                  setInteractionsLoading(false);
                }
              }}
            >
              <option value="">Seleziona esperienza</option>
              {experiences.map((exp) => (
                <option key={exp.id} value={exp.id}>{exp.name}</option>
              ))}
            </select>
          </div>

          {selectedExp && (
            <div className="px-5 py-3 bg-primary/5 border-b border-primary/10 flex items-center gap-2 text-sm">
              <Bot size={14} className="text-primary" />
              <span className="text-text-secondary">Genius Loci:</span>
              <span className="font-medium text-text-primary">{selectedExp.nomeChatbot || 'Non configurato'}</span>
            </div>
          )}

          <div className="divide-y divide-border max-h-64 overflow-y-auto">
            {(loading || interactionsLoading)
              ? Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="p-3.5 flex items-center gap-3 animate-pulse">
                    <div className="w-8 h-8 bg-gray-200 rounded-lg shrink-0" />
                    <div className="flex-1 h-4 bg-gray-200 rounded" />
                  </div>
                ))
              : interactions.length === 0
                ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <MessageCircle size={32} className="text-text-secondary/30 mb-2" />
                    <p className="text-sm text-text-secondary">Nessun intent per questa esperienza</p>
                    <Link to="/interazioni" className="text-xs text-primary mt-2 hover:underline">Aggiungi il primo intent →</Link>
                  </div>
                )
                : interactions.slice(0, 8).map((intent) => (
                  <div key={intent.id} className="p-3.5 flex items-center gap-3 hover:bg-gray-50 transition-colors">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${intent.isFallback ? 'bg-pink-100 text-pink-600' : (intent.latitude != null && intent.longitude != null) ? 'bg-teal-100 text-teal-600' : 'bg-violet-100 text-violet-600'}`}>
                      {intent.isFallback ? <Sparkles size={14} /> : (intent.latitude != null && intent.longitude != null) ? <MapPinned size={14} /> : <MessageCircle size={14} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">{intent.displayName}</p>
                      {intent.trainingPhraseDTO?.length > 0 && (
                        <p className="text-xs text-text-secondary">{intent.trainingPhraseDTO.length} frasi di addestramento</p>
                      )}
                    </div>
                    {(intent.latitude != null && intent.longitude != null) && (
                      <span className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full shrink-0">POI</span>
                    )}
                  </div>
                ))}
          </div>

          {interactions.length > 8 && (
            <div className="p-3 text-center border-t border-border">
              <Link to="/interazioni" className="text-xs text-primary hover:underline">
                +{interactions.length - 8} altri intent → vedi tutti
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Stato piattaforma */}
      <div className="card p-5">
        <h3 className="font-semibold text-text-primary mb-4">Stato piattaforma</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Backend API', status: !error, desc: '/api (proxy Glooci Platform)' },
            { label: 'Glooci Platform', status: true, desc: 'geniusloci-4f597' },
            { label: 'Motore IA', status: true, desc: 'modello conversazionale attivo' },
          ].map((s) => (
            <div key={s.label} className={`flex items-center gap-3 rounded-xl p-3 ${s.status ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
              {s.status
                ? <CheckCircle size={18} className="text-green-600 shrink-0" />
                : <AlertCircle size={18} className="text-red-600 shrink-0" />}
              <div>
                <p className={`text-sm font-medium ${s.status ? 'text-green-800' : 'text-red-800'}`}>{s.label}</p>
                <p className={`text-xs ${s.status ? 'text-green-600' : 'text-red-600'}`}>{s.status ? s.desc : 'Non raggiungibile'}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Banner guida per nuovi operatori */}
      <div className="rounded-2xl bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/10 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white shrink-0">
          <BookOpen size={20} />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-text-primary">Prima volta sulla piattaforma?</p>
          <p className="text-sm text-text-secondary mt-0.5">Leggi la guida per operatori culturali per capire come creare esperienze, configurare il Genius Loci e gestire gli intent.</p>
        </div>
        <Link to="/documentazione" className="btn-primary inline-flex items-center gap-2 text-sm whitespace-nowrap">
          <Clock size={14} />
          Leggi la guida
        </Link>
      </div>
    </div>
  );
};

export default Home;
